import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  // Set page-specific meta tags
  useEffect(() => {
    document.title = "Privacy Policy - Kalobrate";
    
    // Update Open Graph tags for privacy policy page
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      }
    };
    
    updateMetaTag('og:title', 'Privacy Policy - Kalobrate');
    updateMetaTag('og:description', 'Kalobrate Privacy Policy - How we collect, use, and protect your personal data in compliance with GDPR.');
    updateMetaTag('og:url', 'https://www.kalobrate.com/privacy');
    updateMetaTag('twitter:title', 'Privacy Policy - Kalobrate');
    updateMetaTag('twitter:description', 'Kalobrate Privacy Policy - GDPR compliant data protection and privacy practices.');
    
    return () => {
      // Restore original meta tags when leaving page
      document.title = "Kalobrate | Making Workplace Culture Count";
      updateMetaTag('og:title', 'Kalobrate | Making Workplace Culture Count');
      updateMetaTag('og:description', 'Kalobrate helps mid-sized organisations understand workplace culture, identify hidden pressure and make practical decisions that reduce absence, turnover and performance risk.');
      updateMetaTag('og:url', 'https://www.kalobrate.com');
      updateMetaTag('twitter:title', 'Kalobrate | Making Workplace Culture Count');
      updateMetaTag('twitter:description', 'Kalobrate helps mid-sized organisations understand workplace culture, identify hidden pressure and make practical decisions.');
    };
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/">
                <img src="/assets/kalobrate-wordmark-carbon.png" alt="Kalobrate" className="h-7 w-auto" />
              </a>
            </div>
            <div className="flex items-center">
              <a 
                href="/"
                className="flex items-center text-gray-700 hover:text-carbon transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-carbon mb-4 font-heading">
              Privacy Policy
            </h1>
            <p className="text-gray-600 mb-8">Last updated: July 2026</p>
          </motion.div>

          <motion.div 
            className="prose prose-lg max-w-none"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <div className="bg-golden/10 p-6 mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4 font-heading">1. Who We Are</h2>
              <p className="text-gray-700 mb-3">
                Fred Wellbeing Ltd (trading as Kalobrate) provides workplace culture consultancy, leadership development and training services.
              </p>
              <p className="text-gray-700 mb-3">
                We are registered in England and Wales (Company No. 15174341).
              </p>
              <p className="text-gray-700 mb-3">
                We are registered with the Information Commissioner's Office (ICO Registration No. C1531284).
              </p>
              <p className="text-gray-700 mb-3">
                For the purposes of data protection law, we are the Data Controller of the personal data we process.
              </p>
              <div className="mt-4 pt-4 border-t border-golden/30">
                <p className="text-gray-700 font-semibold mb-2">Contact:</p>
                <p className="text-gray-700">Dr Oliver Bell</p>
                <a href="mailto:ollie@kalobrate.com" className="text-carbon hover:text-golden transition-colors">
                  ollie@kalobrate.com
                </a>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">2. What Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                Depending on how you engage with us, we may collect and process:
              </p>
              
              <h3 className="text-xl font-semibold text-carbon mb-3">Website Contact Form</h3>
              <p className="text-gray-700 mb-3">
                When you submit an enquiry through the website contact form, we collect your name, email address, organisation (if provided) and message. This information is used solely to respond to your enquiry.
              </p>
              <p className="text-gray-700 mb-4">
                Contact form submissions are stored securely and retained for up to 12 months after your last correspondence, after which they are deleted unless the enquiry has led to an ongoing client relationship.
              </p>

              <h3 className="text-xl font-semibold text-carbon mb-3">Basic Contact Information</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Name</li>
                <li>Job title</li>
                <li>Organisation name</li>
                <li>Email address</li>
                <li>Telephone number</li>
              </ul>

              <h3 className="text-xl font-semibold text-carbon mb-3">Project Participation Data</h3>
              <p className="text-gray-700 mb-3">
                Where you participate in funded programmes, workshops, surveys or focus groups, we may collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Attendance records</li>
                <li>Survey responses</li>
                <li>Leadership confidence measures</li>
                <li>Feedback and evaluation responses</li>
                <li>Information shared during facilitated discussions</li>
              </ul>

              <h3 className="text-xl font-semibold text-carbon mb-3">Special Category Data</h3>
              <p className="text-gray-700 mb-3">
                Some programmes may involve the collection of wellbeing and mental health information. This may include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Self-reported mental wellbeing scores</li>
                <li>Health-related information</li>
                <li>Experiences of stress, anxiety or low mood</li>
                <li>Contextual lived experiences relevant to workplace wellbeing</li>
              </ul>
              <p className="text-gray-700 mb-2">
                This is classified as special category data under UK GDPR.
              </p>
              <p className="text-gray-700">
                We only process this where necessary and with appropriate safeguards.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">3. How We Use Your Data</h2>
              <p className="text-gray-700 mb-4">We use personal data to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Deliver workplace wellbeing and leadership programmes</li>
                <li>Measure baseline and follow-up outcomes</li>
                <li>Evaluate programme effectiveness</li>
                <li>Monitor attendance and participation</li>
                <li>Signpost individuals to appropriate support services</li>
                <li>Produce anonymised impact reports</li>
                <li>Comply with contractual and funding obligations</li>
                <li>Improve future service delivery</li>
              </ul>
              <p className="text-gray-700 mb-2">
                Where projects are funded by public bodies, we may report anonymised and aggregated data to the funder.
              </p>
              <p className="text-gray-700 font-semibold">
                No identifiable personal data relating to participants is shared with funders.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">4. Lawful Basis for Processing</h2>
              <p className="text-gray-700 mb-4">
                We process personal data under the following lawful bases:
              </p>
              
              <h3 className="text-xl font-semibold text-carbon mb-3">Article 6 UK GDPR</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Legitimate interests (delivery and improvement of services)</li>
                <li>Contractual necessity (where services are delivered to organisations)</li>
                <li>Consent (where appropriate, including surveys and focus groups)</li>
              </ul>

              <h3 className="text-xl font-semibold text-carbon mb-3">Article 9 UK GDPR (Special Category Data)</h3>
              <p className="text-gray-700 mb-3">
                Where we process health or wellbeing data, we rely on:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Explicit consent from the participant</li>
              </ul>
              <p className="text-gray-700">
                Participants may withdraw consent at any time.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">5. Sharing Your Data</h2>
              <p className="text-gray-700 mb-4">We do not sell personal data.</p>
              <p className="text-gray-700 mb-3">We may share data with:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Trusted service providers (e.g. secure survey platforms, transcription services)</li>
                <li>Professional advisers (where legally required)</li>
                <li>Public funders (anonymised, aggregated data only)</li>
              </ul>
              <p className="text-gray-700">
                We ensure appropriate data processing agreements are in place where required.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">6. Website Analytics and Cookies</h2>
              <p className="text-gray-700 mb-3">
                This website uses analytics tools to understand how visitors use the site. These tools are <strong>only activated after you explicitly accept cookies</strong> through the cookie consent banner shown on your first visit.
              </p>
              
              <h3 className="text-xl font-semibold text-carbon mb-3">Google Analytics (GA4)</h3>
              <p className="text-gray-700 mb-3">
                Google Analytics collects anonymous, aggregated data about how visitors interact with the website. This includes pages visited, time on site, device type, browser, approximate geographic region and referral source. Google Analytics does not collect your name, email address or other identifying information from this website.
              </p>
              <p className="text-gray-700 mb-3">
                Google Analytics data may be processed on servers outside the UK. Google operates under Standard Contractual Clauses and their own data processing terms. For more information, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-carbon hover:text-golden transition-colors underline">Google's Privacy Policy</a>.
              </p>

              <h3 className="text-xl font-semibold text-carbon mb-3">PostHog</h3>
              <p className="text-gray-700 mb-3">
                PostHog provides similar anonymous usage analytics. Data is processed on servers in the United States. PostHog operates under Standard Contractual Clauses. No personally identifiable information is collected.
              </p>

              <h3 className="text-xl font-semibold text-carbon mb-3">Your choices</h3>
              <p className="text-gray-700 mb-3">
                You can accept or reject analytics cookies at any time. If you reject cookies, no analytics data is collected and the tracking scripts are not loaded. You can change your preference through the <a href="/cookie-policy" className="text-carbon hover:text-golden transition-colors underline">Cookie Policy</a> page.
              </p>
              <p className="text-gray-700 mb-3">
                If your browser sends a Do Not Track (DNT) signal, analytics cookies are not loaded by default.
              </p>
              <p className="text-gray-700">
                The lawful basis for processing analytics data is consent (Article 6(1)(a) UK GDPR).
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">7. Data Storage and Security</h2>
              <p className="text-gray-700 mb-3">
                We store data securely using encrypted and password-protected systems.
              </p>
              <p className="text-gray-700 mb-3">
                Access is restricted to authorised individuals only.
              </p>
              <p className="text-gray-700">
                We take appropriate technical and organisational measures to protect personal data against unauthorised access, loss or misuse.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">8. International Transfers</h2>
              <p className="text-gray-700 mb-3">
                We do not transfer personal data outside the UK unless appropriate safeguards are in place in accordance with UK GDPR.
              </p>
              <p className="text-gray-700">
                Where analytics cookies are accepted, anonymous usage data may be processed by Google (Google Analytics) and PostHog on servers outside the UK. These transfers are covered by Standard Contractual Clauses.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">9. How Long We Keep Your Data</h2>
              <p className="text-gray-700 mb-3">
                Project-related personal data will be retained securely until 31 March 2028 to meet contractual audit and evaluation requirements, unless a longer retention period is required by law.
              </p>
              <p className="text-gray-700 mb-3">
                Website contact form submissions are retained for up to 12 months after the last correspondence, then securely deleted.
              </p>
              <p className="text-gray-700 mb-3">
                After the relevant retention period, all data will be securely deleted or anonymised.
              </p>
              <p className="text-gray-700">
                Marketing contact data is retained until you unsubscribe.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-carbon mb-4">10. Your Rights</h2>
              <p className="text-gray-700 mb-4">Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Access your personal data</li>
                <li>Request correction</li>
                <li>Request deletion</li>
                <li>Restrict processing</li>
                <li>Object to processing</li>
                <li>Withdraw consent</li>
                <li>Lodge a complaint with the Information Commissioner's Office</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, contact: 
                <a href="mailto:ollie@kalobrate.com" className="text-carbon hover:text-golden transition-colors ml-1">
                  ollie@kalobrate.com
                </a>
              </p>
            </div>

            <div className="bg-carbon text-white p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
              <p className="text-white/90">
                We may update this policy from time to time. The latest version will always be available on our website.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;